import { useEffect, useState } from 'react'
import type { ContatoItem } from '@hooks/useContatos.js'
import type { ContatoPapel } from '@services/db.js'
import type { NovoContato } from '@services/gestao.js'
import { Modal } from '@components/primitives/Modal.js'
import { Button } from '@components/primitives/Button.js'
import { Input } from '@components/primitives/Input.js'
import { Select } from '@components/primitives/Select.js'
import { Textarea } from '@components/primitives/Textarea.js'
import { CONTATO_PAPEIS } from '@utils/constants.js'

interface Props {
  open: boolean
  initial?: ContatoItem | null
  /** Pré-seleciona o papel (ex.: ao criar a partir de um contrato). */
  papelInicial?: ContatoPapel
  onClose: () => void
  onSalvar: (input: NovoContato) => Promise<void>
  onEditar?: (id: number, input: Partial<NovoContato>) => Promise<void>
}

const EMPTY = {
  nome: '',
  papel: 'pedreiro' as ContatoPapel,
  telefone: '',
  empresa: '',
  documento: '',
  avaliacao: 0,
  observacoes: '',
}

export function ContatoFormModal({
  open,
  initial,
  papelInicial,
  onClose,
  onSalvar,
  onEditar,
}: Props) {
  const [form, setForm] = useState(EMPTY)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!open) return
    if (initial) {
      setForm({
        nome: initial.nome,
        papel: initial.papel,
        telefone: initial.telefone ?? '',
        empresa: initial.empresa ?? '',
        documento: initial.documento ?? '',
        avaliacao: initial.avaliacao ?? 0,
        observacoes: initial.observacoes ?? '',
      })
    } else {
      setForm({ ...EMPTY, papel: papelInicial ?? EMPTY.papel })
    }
  }, [open, initial, papelInicial])

  const set = <K extends keyof typeof EMPTY>(k: K, v: (typeof EMPTY)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const canSubmit = form.nome.trim().length > 1

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    if (!canSubmit) return
    setSalvando(true)
    const input: NovoContato = {
      nome: form.nome.trim(),
      papel: form.papel,
      ...(form.telefone.trim() ? { telefone: form.telefone.trim() } : {}),
      ...(form.empresa.trim() ? { empresa: form.empresa.trim() } : {}),
      ...(form.documento.trim() ? { documento: form.documento.trim() } : {}),
      ...(form.avaliacao ? { avaliacao: form.avaliacao as 1 | 2 | 3 | 4 | 5 } : {}),
      ...(form.observacoes.trim() ? { observacoes: form.observacoes.trim() } : {}),
    }
    if (initial && onEditar) {
      await onEditar(initial.id, {
        ...input,
        telefone: input.telefone ?? '',
        empresa: input.empresa ?? '',
        documento: input.documento ?? '',
        observacoes: input.observacoes ?? '',
        ...(input.avaliacao ? {} : { avaliacao: undefined }),
      })
    } else {
      await onSalvar(input)
    }
    setSalvando(false)
    onClose()
  }

  return (
    <Modal
      open={open}
      title={initial ? 'Editar contato' : 'Novo contato'}
      onClose={onClose}
      actions={
        <div className='cf3-modal-actions'>
          <Button variant='ghost' onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant='primary'
            type='submit'
            form='contato-form'
            loading={salvando}
            disabled={!canSubmit}
          >
            Salvar
          </Button>
        </div>
      }
    >
      <form id='contato-form' className='cf3-gestao-form' onSubmit={handleSubmit}>
        <Input
          label='Nome'
          value={form.nome}
          placeholder='Ex.: Seu João'
          required
          onChange={(e) => set('nome', e.target.value)}
        />
        <Select
          label='Função na obra'
          value={form.papel}
          options={CONTATO_PAPEIS}
          onChange={(e) => set('papel', e.target.value as ContatoPapel)}
        />
        <Input
          label='Telefone / WhatsApp'
          type='tel'
          value={form.telefone}
          placeholder='(11) 99999-9999'
          hint='Com DDD. Usado para ligar e abrir o WhatsApp.'
          onChange={(e) => set('telefone', e.target.value)}
        />
        <div className='cf3-gestao-form__row'>
          <Input
            label='Empresa (opcional)'
            value={form.empresa}
            placeholder='Ex.: Depósito São João'
            onChange={(e) => set('empresa', e.target.value)}
          />
          <Input
            label='CPF/CNPJ (opcional)'
            value={form.documento}
            placeholder='Para contrato e NF'
            onChange={(e) => set('documento', e.target.value)}
          />
        </div>
        <div className='cf-field'>
          <span className='cf-field__label'>Avaliação</span>
          <div className='cf3-estrelas' role='radiogroup' aria-label='Avaliação de 1 a 5'>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type='button'
                role='radio'
                aria-checked={form.avaliacao === n}
                aria-label={`${n} de 5`}
                className={`cf3-estrelas__btn${form.avaliacao >= n ? ' cf3-estrelas__btn--on' : ''}`}
                onClick={() => set('avaliacao', form.avaliacao === n ? 0 : n)}
              >
                ★
              </button>
            ))}
          </div>
        </div>
        <Textarea
          label='Observações'
          value={form.observacoes}
          rows={2}
          placeholder='Indicado por…, especialidade, horários…'
          onChange={(e) => set('observacoes', e.target.value)}
        />
      </form>
    </Modal>
  )
}
